import React, { useEffect, useState, useCallback } from "react";
import { api } from "../api/client";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { DollarSign, CreditCard, AlertTriangle, TrendingUp, TrendingDown, RefreshCw, Info } from "lucide-react";

const AGING_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#7C3AED"];

function StatCard({ title, value, change_pct, icon: Icon, color, loading }) {
  const isPos = change_pct >= 0;
  const colorMap = {
    green: { bg: "bg-emerald-50", ic: "text-emerald-600", border: "border-emerald-100" },
    blue: { bg: "bg-blue-50", ic: "text-blue-600", border: "border-blue-100" },
    red: { bg: "bg-red-50", ic: "text-red-600", border: "border-red-100" },
  };
  const c = colorMap[color] || colorMap.blue;
  return (
    <div
      data-testid={`widget-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2.5 rounded-lg ${c.bg} border ${c.border}`}>
          <Icon size={18} className={c.ic} strokeWidth={1.75} />
        </div>
        {change_pct !== undefined && (
          <span className={`flex items-center gap-1 text-xs font-semibold ${isPos ? "text-emerald-600" : "text-red-500"}`}>
            {isPos ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(change_pct).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-900 stat-value">
        {loading ? <span className="inline-block w-24 h-7 bg-slate-100 rounded animate-pulse" /> : value}
      </p>
      <p className="text-sm text-slate-500 mt-1">{title}</p>
    </div>
  );
}

function DepositsTable({ rows, loading }) {
  if (loading) return <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />;
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <table className="w-full text-sm" data-testid="recent-deposits-table">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            {["ID", "Patient", "Amount", "Date", "Status", "Facility"].map((h) => (
              <th key={h} className="p-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
              <td className="p-4 font-mono text-xs text-slate-700">{r.id}</td>
              <td className="p-4 text-slate-700">{r.patient_id}</td>
              <td className="p-4 font-semibold text-slate-900">${Number(r.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              <td className="p-4 text-slate-500">{new Date(r.date).toLocaleDateString()}</td>
              <td className="p-4">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">{r.status}</span>
              </td>
              <td className="p-4 text-slate-600">{r.facility}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailuresTable({ rows, loading }) {
  if (loading) return <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />;
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <table className="w-full text-sm" data-testid="payment-failures-table">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            {["ID", "Patient", "Amount", "Reason", "Date", "Facility"].map((h) => (
              <th key={h} className="p-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
              <td className="p-4 font-mono text-xs text-slate-700">{r.id}</td>
              <td className="p-4 text-slate-700">{r.patient_id}</td>
              <td className="p-4 font-semibold text-slate-900">${Number(r.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              <td className="p-4">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{r.reason}</span>
              </td>
              <td className="p-4 text-slate-500">{new Date(r.date).toLocaleDateString()}</td>
              <td className="p-4 text-slate-600">{r.facility}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CollectionsDashboard() {
  const [widgets, setWidgets] = useState([]);
  const [wdata, setWdata] = useState({});
  const [loading, setLoading] = useState(true);
  const [mockMode, setMockMode] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const dashRes = await api.get("/dashboard/collections");
      const ws = dashRes.data.widgets || [];
      setWidgets(ws);
      setMockMode(dashRes.data.bq_mode === "mock");

      const results = await Promise.all(
        ws.map((w) => api.post("/widgets/run", { widgetId: w.id, params: {} }).then((r) => [w.id, r.data.data]))
      );
      setWdata(Object.fromEntries(results));
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Dashboard load error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const depositsToday = wdata["deposits_today"] || {};
  const agingData = wdata["aging_buckets"] || [];

  return (
    <div className="flex-1 overflow-auto">
      <div className="sticky top-0 z-10 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/80 px-8 backdrop-blur-md">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Collections Dashboard
          </h1>
          {lastRefresh && <p className="text-xs text-slate-400">Last updated {lastRefresh.toLocaleTimeString()}</p>}
        </div>
        <div className="flex items-center gap-3">
          {mockMode && (
            <span data-testid="mock-badge" className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full font-medium">
              <Info size={12} /> Sample data
            </span>
          )}
          <button
            data-testid="refresh-button"
            onClick={fetchAll}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Top row: stat + pie chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <StatCard
            title="Deposits Today"
            value={`$${Number(depositsToday.value || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
            change_pct={depositsToday.change_pct}
            icon={DollarSign}
            color="green"
            loading={loading}
          />

          {/* Aging Buckets */}
          <div
            data-testid="widget-aging_buckets"
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-base font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Aging Buckets
            </h3>
            {loading ? (
              <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={agingData}
                    dataKey="amount"
                    nameKey="bucket"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    {agingData.map((entry, i) => (
                      <Cell key={entry.name || `aging-${i}`} fill={AGING_COLORS[i % AGING_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => `$${v.toLocaleString()}`}
                    contentStyle={{ border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "12px" }}
                  />
                  <Legend
                    formatter={(v) => <span className="text-xs text-slate-600">{v}</span>}
                    iconSize={10}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent deposits */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="widget-recent_deposits">
          <h3 className="text-base font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Recent Deposits
          </h3>
          <DepositsTable rows={wdata["recent_deposits"]} loading={loading} />
        </div>

        {/* Payment failures */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="widget-payment_failures">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-red-500" />
            <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Payment Failures
            </h3>
          </div>
          <FailuresTable rows={wdata["payment_failures"]} loading={loading} />
        </div>
      </div>
    </div>
  );
}
